import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import type { Content, Editor } from "@tiptap/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSend } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  Currency,
  NeedPriority,
  Regulation,
  useSubmitNeedMutation,
  type ContractNature,
  type NeedDto,
  type NeedPatchDto,
  type PersonDto,
} from "../../../autogen/bff-api";
import { AiDraftModal } from "../../../common/ai/AiDraftModal";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useEditor, useFieldErrorTranslation } from "../../../common/editing/save-coordinator";
import { useApiError } from "../../../common/errors/useApiError";
import { NumericInput } from "../../../common/input/NumericInput";
import { RadioOptionGroup } from "../../../common/input/RadioOptionGroup";
import { TipTap, parseTiptapContent } from "../../../common/input/TipTap/TipTap";
import { getMarkdownFromTiptapContent } from "../../../common/input/TipTap/markdownUtils";
import { PersonSelector } from "../../../common/persons/PersonSelector";
import { foaPartBanner } from "../../sourcing-events/buyer/wizard/utils/foaPartDisplay";
import { CONTRACT_NATURE_OPTIONS } from "../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { NeedDocuments } from "./NeedDocuments";
import { NeedTypeAiSuggestion } from "./NeedTypeAiSuggestion";
import { buildNeedDescriptionMessages } from "./needDescriptionAiPrompt";
import { NEED_PRIORITIES, useNeedPriorityLabel } from "./useNeedPriorityLabel";

interface Props {
  needId: string;
  need: NeedDto;
  refetch: () => void;
  onSubmitted: () => void;
}

export const NeedForm = ({ needId, need, refetch, onSubmitted }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { state, updateField, entityStatus, fieldStatus, fieldError } = useEditor<NeedDto, NeedPatchDto>(needId);
  const formatFieldError = useFieldErrorTranslation();
  const priorityLabel = useNeedPriorityLabel();
  const apiError = useApiError();
  const [submitNeed] = useSubmitNeedMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDescriptionAi, setShowDescriptionAi] = useState(false);
  const [pickedContactPerson, setPickedContactPerson] = useState<PersonDto | null>(null);
  const descriptionEditorRef = useRef<Editor | null>(null);

  const statusRef = useRef(entityStatus);
  statusRef.current = entityStatus;

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const estimatedValue = state.estimatedValue?.value ?? null;
  // A need is pre-procurement and carries no regulation; the BFF classifies it under FOA.
  const banner = foaPartBanner({
    regulation: Regulation.Foa,
    foaPart: state.foaPart ?? null,
    estimatedValueNok: estimatedValue != null ? Number(estimatedValue) : null,
    contractNature: state.procurementType,
  });
  const titleError = fieldError("title");
  const estimatedValueError = fieldError("estimatedValue.value");
  // updateField patches contactPersonId only, so the resolved person lags until the save returns.
  const contactPerson =
    state.contactPerson?.id === state.contactPersonId ? state.contactPerson : pickedContactPerson;

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // Flush any pending debounced edits, then wait for the save queue to drain
    // so submit validates against the latest persisted state.
    updateField({}, { debounceMs: 0 });
    const start = Date.now();
    while ((statusRef.current === "pending" || statusRef.current === "saving") && Date.now() - start < 5000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    try {
      await submitNeed({ needId }).unwrap();
      onSubmitted();
    } catch (e) {
      apiError.trigger(e);
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="container.md" mx="auto" pb="10">
      <Heading as="h1" size="lg" color="smText" mb="1">
        {t("Report a need")}
      </Heading>
      <Text color="smMuted" mb="6">
        {t("Register a new procurement need.")}
      </Text>

      <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="lg" p="6">
        <FormControl isRequired mb="6" isInvalid={!!titleError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Title")}</FormLabel>
            {isSaving("title") && <Spinner size="xs" />}
          </Flex>
          <Input
            value={state.title ?? ""}
            placeholder={t("Briefly describe the need") ?? undefined}
            onChange={(e) => updateField({ title: e.target.value }, { debounceMs: 300 })}
          />
          {titleError && <FormErrorMessage>{formatFieldError(titleError)}</FormErrorMessage>}
        </FormControl>

        <FormControl isRequired mb="6">
          <FormLabel>
            {t("Description")} {isSaving("description") && <Spinner size="xs" />}
          </FormLabel>
          <TipTap
            content={state.description ?? ""}
            onUpdate={async (value) => updateField({ description: value })}
            editable={true}
            debounceWait={300}
            onEditorReady={(editor) => {
              descriptionEditorRef.current = editor;
            }}
            onAutofill={() => setShowDescriptionAi(true)}
          />
          <FormHelperText>{t("Describe the need, its scope and any background")}</FormHelperText>
          {showDescriptionAi && (
            <AiDraftModal
              title={t("Suggest description with AI")}
              feature="need-description"
              buildMessages={() =>
                buildNeedDescriptionMessages({
                  title: state.title ?? "",
                  currentDescription: getMarkdownFromTiptapContent(state.description ?? ""),
                  language: i18n.language.startsWith("no") ? "no" : "en",
                })
              }
              onClose={() => setShowDescriptionAi(false)}
              onInsert={(json) => {
                descriptionEditorRef.current?.commands.setContent(parseTiptapContent(json) as Content, {
                  emitUpdate: true,
                });
                setShowDescriptionAi(false);
              }}
            />
          )}
        </FormControl>

        <FormControl isRequired mb="6" isInvalid={!!estimatedValueError}>
          <FormLabel>
            {t("Estimated value")} {isSaving("estimatedValue") && <Spinner size="xs" />}
          </FormLabel>
          <InputGroup>
            <NumericInput
              numericValue={estimatedValue != null ? Number(estimatedValue) : null}
              placeholder="0"
              onNumericChange={(value) =>
                updateField({ estimatedValue: { value, currency: Currency.Nok } }, { debounceMs: 300 })
              }
            />
            <InputRightAddon>{t("NOK")}</InputRightAddon>
          </InputGroup>
          {estimatedValueError && <FormErrorMessage>{formatFieldError(estimatedValueError)}</FormErrorMessage>}
          {banner && !estimatedValueError && (
            <Alert status={banner.status} mt="2" rounded="md" fontSize="sm">
              <AlertIcon />
              {t(banner.messageKey)}
            </Alert>
          )}
        </FormControl>

        <FormControl mb="6">
          <FormLabel>
            {t("Initial value assessment")} {isSaving("valueAssessment") && <Spinner size="xs" />}
          </FormLabel>
          <TipTap
            content={state.valueAssessment ?? ""}
            onUpdate={async (value) => updateField({ valueAssessment: value })}
            editable={true}
            debounceWait={300}
          />
          <FormHelperText>{t("The basis for the initial estimate. Final value is set in the procurement.")}</FormHelperText>
        </FormControl>

        <FormControl mb="6">
          <FormLabel>
            {t("Budget line")} {isSaving("budgetLine") && <Spinner size="xs" />}
          </FormLabel>
          <Input
            value={state.budgetLine ?? ""}
            placeholder={t("E.g. 4300 Consulting services") ?? undefined}
            onChange={(e) => updateField({ budgetLine: e.target.value }, { debounceMs: 300 })}
          />
        </FormControl>

        <FormControl mb="6">
          <FormLabel>{t("Attachments")}</FormLabel>
          <NeedDocuments need={need} refetch={refetch} canEdit={need.canEdit ?? true} />
        </FormControl>

        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="5" mb="6">
          <FormControl isRequired>
            <FormLabel>
              {t("Desired delivery date")} {isSaving("deliveryDate") && <Spinner size="xs" />}
            </FormLabel>
            <Input
              type="date"
              value={state.deliveryDate ?? ""}
              onChange={(e) => updateField({ deliveryDate: e.target.value || null })}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>
              {t("Department")} {isSaving("departmentId") && <Spinner size="xs" />}
            </FormLabel>
            <Select
              placeholder={t("Select department") ?? undefined}
              value={state.departmentId ?? ""}
              onChange={(e) => updateField({ departmentId: e.target.value || null })}
            >
              {selectedOrg.departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>
              {t("Priority")} {isSaving("priority") && <Spinner size="xs" />}
            </FormLabel>
            <Select
              placeholder={t("Select priority") ?? undefined}
              value={state.priority ?? ""}
              onChange={(e) => updateField({ priority: (e.target.value || null) as NeedPriority | null })}
            >
              {NEED_PRIORITIES.map((priority) => (
                <option key={priority} value={priority}>
                  {priorityLabel(priority)}
                </option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>

        <FormControl isRequired mb="6">
          <FormLabel>
            {t("What does the procurement concern?")} {isSaving("procurementType") && <Spinner size="xs" />}
          </FormLabel>
          <RadioOptionGroup
            options={CONTRACT_NATURE_OPTIONS}
            value={state.procurementType ?? ""}
            onChange={(value) => updateField({ procurementType: value as ContractNature })}
          />
          {selectedOrg.subscription.hasAiAccess && <NeedTypeAiSuggestion needId={needId} />}
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            {t("Contact person")} {isSaving("contactPersonId") && <Spinner size="xs" />}
          </FormLabel>
          <PersonSelector
            size="md"
            organizationIds={[selectedOrg.id]}
            selectedPerson={contactPerson}
            placeholder={t("Select contact person") ?? undefined}
            onChange={(person) => {
              setPickedContactPerson(person);
              updateField({ contactPersonId: person?.id ?? null });
            }}
          />
        </FormControl>
      </Box>

      <Flex justify="space-between" mt="6">
        <Button variant="outline" onClick={() => navigate(-1)}>
          {t("Cancel")}
        </Button>
        <Button colorScheme="teal" leftIcon={<FiSend />} onClick={handleSubmit} isLoading={isSubmitting}>
          {t("Submit need")}
        </Button>
      </Flex>
    </Box>
  );
};
